/**
 * Small pieces every pharmacy screen shares: the figure tile, the status
 * badges, the table frame, the section tabs, and the error text an API
 * refusal should show. Kept here so the screens read alike.
 */

import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { RX_CLASS_LABELS, type RxClass } from '@cenox/shared';
import { ApiError } from '../../lib/api';
import { Badge, cn } from '../../components/ui';

export function Tile({
  label,
  value,
  sub,
  tone = 'plain',
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  tone?: 'plain' | 'good' | 'warn' | 'bad';
}): ReactNode {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3.5">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p
        className={cn(
          'mt-1 text-lg font-semibold tabular-nums',
          tone === 'good' && 'text-emerald-700',
          tone === 'warn' && 'text-amber-700',
          tone === 'bad' && 'text-red-700',
          tone === 'plain' && 'text-slate-900',
        )}
      >
        {value}
      </p>
      {sub && <p className="mt-0.5 text-xs text-slate-500">{sub}</p>}
    </div>
  );
}

export function RxBadge({ rxClass }: { rxClass: string }): ReactNode {
  const tone = rxClass === 'restricted' ? 'danger' : rxClass === 'prescription_only' ? 'warning' : rxClass === 'pharmacy_only' ? 'info' : 'neutral';
  const label = RX_CLASS_LABELS[rxClass as RxClass] ?? rxClass;
  return <Badge tone={tone}>{rxClass === 'prescription_only' ? 'POM' : rxClass === 'restricted' ? 'Restricted' : rxClass === 'pharmacy_only' ? 'P' : 'OTC'}<span className="sr-only"> — {label}</span></Badge>;
}

const STATUS_TONES: Record<string, 'neutral' | 'success' | 'warning' | 'danger' | 'info'> = {
  received: 'info',
  approved: 'success',
  rejected: 'danger',
  partially_dispensed: 'warning',
  dispensed: 'success',
  cancelled: 'neutral',
  draft: 'neutral',
  ordered: 'info',
  partially_received: 'warning',
  closed: 'neutral',
  dispatched: 'info',
  discrepancy: 'danger',
  reconciled: 'success',
  pending: 'warning',
  confirmed: 'success',
  failed: 'danger',
  open: 'warning',
  decided: 'success',
  paid: 'success',
  partial: 'warning',
  unpaid: 'danger',
  posted: 'success',
  quarantine: 'warning',
  recall: 'danger',
  restock: 'success',
  dispose: 'danger',
};

export function StatusBadge({ status }: { status: string | null | undefined }): ReactNode {
  if (!status) return null;
  return <Badge tone={STATUS_TONES[status] ?? 'neutral'}>{status.replace(/_/g, ' ')}</Badge>;
}

export function ExpiryBadge({ expiryDate, expired, nearExpiry }: { expiryDate: string; expired?: boolean; nearExpiry?: boolean }): ReactNode {
  return (
    <span className={cn('tabular-nums', expired ? 'font-semibold text-red-700' : nearExpiry ? 'font-medium text-amber-700' : 'text-slate-700')}>
      {expiryDate}
      {expired ? ' · expired' : nearExpiry ? ' · soon' : ''}
    </span>
  );
}

/** A responsive table frame: scrolls sideways on a phone rather than squashing. */
export function Table({ head, children, minWidth = 640 }: { head: ReactNode[]; children: ReactNode; minWidth?: number }): ReactNode {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" style={{ minWidth }}>
        <thead className="border-y border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
          <tr>
            {head.map((h, i) => (
              <th key={i} className="px-3 py-2 font-medium first:pl-4 last:pr-4">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">{children}</tbody>
      </table>
    </div>
  );
}

export function Td({ children, className }: { children?: ReactNode; className?: string }): ReactNode {
  return <td className={cn('px-3 py-2 align-top first:pl-4 last:pr-4', className)}>{children}</td>;
}

/** Section tabs under a page header — links, so each section has its own URL. */
export function SectionTabs({ tabs }: { tabs: Array<{ to: string; label: string; end?: boolean }> }): ReactNode {
  return (
    <nav aria-label="Sections" className="-mx-4 mb-5 overflow-x-auto px-4 sm:mx-0 sm:px-0">
      <div className="inline-flex gap-1 rounded-lg border border-slate-200 bg-white p-1">
        {tabs.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            end={t.end ?? true}
            className={({ isActive }) =>
              cn(
                'whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium',
                isActive ? 'bg-brand-600 text-white' : 'text-slate-600 hover:bg-slate-50',
              )
            }
          >
            {t.label}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

/** The message an API refusal carries, for showing next to the action that caused it. */
export function errorText(error: unknown): string | null {
  if (!error) return null;
  if (error instanceof ApiError) return error.message;
  return error instanceof Error ? error.message : 'Something went wrong';
}

export function ErrorLine({ error }: { error: unknown }): ReactNode {
  const text = errorText(error);
  if (!text) return null;
  return (
    <p role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
      {text}
    </p>
  );
}

/** Whole base units shown in the largest units that divide them, e.g. "2 packs + 3 strips + 4 tablets". */
export function describeQuantity(base: number, units: Array<{ name: string; baseQuantity: number }>): string {
  if (!units.length) return String(base);
  const sorted = [...units].sort((a, b) => b.baseQuantity - a.baseQuantity);
  let left = base;
  const parts: string[] = [];
  for (const u of sorted) {
    const n = Math.floor(left / u.baseQuantity);
    if (n > 0) {
      parts.push(`${n} ${u.name}${n === 1 ? '' : 's'}`);
      left -= n * u.baseQuantity;
    }
  }
  return parts.length ? parts.join(' + ') : `0 ${sorted[sorted.length - 1]!.name}s`;
}
